import type { NIBContributionPreview, NIBCategory } from '@/types';
import { NIB_CATEGORY_INFO } from '@/types';
import { LoadingSpinner } from '@components/common/LoadingSpinner';
import {
  CurrencyDollarIcon,
  ScissorsIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

interface ContributionPreviewProps {
  preview: NIBContributionPreview | null;
  category?: NIBCategory; // Employee category for rate/cap display
  grossWages?: number; // Original gross wages (since API doesn't return this)
  isLoading?: boolean;
  showDetails?: boolean;
}

export const ContributionPreview = ({
  preview,
  category,
  grossWages,
  isLoading = false,
  showDetails = false,
}: ContributionPreviewProps) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-BS', {
      style: 'currency',
      currency: 'BSD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatPercentage = (rate: number) => {
    return `${(rate * 100).toFixed(2)}%`;
  };

  if (isLoading) {
    return (
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="p-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <LoadingSpinner size="lg" />
              <p className="mt-3 text-sm text-gray-500">Calculating...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="p-6">
          <div className="text-center py-12">
            <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">
              Enter wages to see contribution calculation
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="px-6 py-5 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">
          Contribution Preview
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Live calculation based on current NIB rates
        </p>
      </div>

      <div className="p-6 space-y-6">
        {/* Wage Information */}
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Gross Wages</span>
            <span className="font-medium text-gray-900">
              {formatCurrency(grossWages ?? preview.gross_wages)}
            </span>
          </div>

          {preview.wage_was_truncated && (
            <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 p-3 rounded-md">
              <ScissorsIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Wage Truncated</p>
                <p className="text-xs text-amber-600 mt-0.5">
                  Cents removed for NIB calculation
                </p>
              </div>
            </div>
          )}

          {preview.wage_was_capped && (
            <div className="flex items-start gap-2 text-sm text-blue-700 bg-blue-50 p-3 rounded-md">
              <CheckCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Weekly Cap Applied</p>
                <p className="text-xs text-blue-600 mt-0.5">
                  Maximum insurable wage:{' '}
                  {preview.calculation_details
                    ? formatCurrency(preview.calculation_details.weekly_cap)
                    : 'N/A'}
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
            <span className="font-medium text-gray-700">Insurable Wages</span>
            <span className="font-semibold text-gray-900">
              {formatCurrency(preview.insurable_wages)}
            </span>
          </div>
        </div>

        {/* Contributions Breakdown */}
        <div className="space-y-3 pt-4 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-900">Contributions</h4>

          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Employee</span>
                {showDetails && preview.calculation_details && (
                  <span className="text-xs text-gray-400">
                    (
                    {formatPercentage(
                      preview.calculation_details.employee_rate
                    )}
                    )
                  </span>
                )}
              </div>
              <span className="font-medium text-gray-900">
                {formatCurrency(preview.employee_contribution)}
              </span>
            </div>

            <div className="flex justify-between items-center text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Employer</span>
                {showDetails && preview.calculation_details && (
                  <span className="text-xs text-gray-400">
                    (
                    {formatPercentage(
                      preview.calculation_details.employer_rate
                    )}
                    )
                  </span>
                )}
              </div>
              <span className="font-medium text-gray-900">
                {formatCurrency(preview.employer_contribution)}
              </span>
            </div>

            {preview.gratuity !== undefined && preview.gratuity > 0 && (
              <div className="flex justify-between items-center text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Gratuity</span>
                  {showDetails && preview.calculation_details && (
                    <span className="text-xs text-gray-400">
                      (
                      {formatPercentage(
                        preview.calculation_details.gratuity_rate
                      )}
                      )
                    </span>
                  )}
                </div>
                <span className="font-medium text-gray-900">
                  {formatCurrency(preview.gratuity_contribution)}
                </span>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center text-base pt-3 border-t border-gray-300">
            <span className="font-semibold text-gray-900">
              Total Contribution
            </span>
            <span className="font-bold text-primary-600 text-lg">
              {formatCurrency(preview.total_contribution)}
            </span>
          </div>
        </div>

        {/* Rate Information (if showDetails) */}
        {showDetails && (
          <div className="pt-4 border-t border-gray-200">
            <h4 className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-3">
              NIB Rates & Caps
            </h4>
            <dl className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <dt className="text-gray-500">Employee Rate</dt>
                <dd className="font-medium text-gray-900">
                  {category
                    ? formatPercentage(NIB_CATEGORY_INFO[category].employeeRate)
                    : 'Varies by category'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Employer Rate</dt>
                <dd className="font-medium text-gray-900">
                  {category
                    ? formatPercentage(NIB_CATEGORY_INFO[category].employerRate)
                    : 'Varies by category'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Weekly Cap</dt>
                <dd className="font-medium text-gray-900">
                  {category
                    ? formatCurrency(NIB_CATEGORY_INFO[category].weeklyCap)
                    : 'Varies by category'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Monthly Cap</dt>
                <dd className="font-medium text-gray-900">
                  {category
                    ? formatCurrency(NIB_CATEGORY_INFO[category].monthlyCap)
                    : 'Varies by category'}
                </dd>
              </div>
            </dl>
          </div>
        )}
      </div>
    </div>
  );
};
